import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Eye, ShieldCheck, BellRinging, Warning } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CalendarSubscribe } from "@/components/CalendarSubscribe";

const MODES = [
  { value: "all", label: "Whole shop" },
  { value: "own", label: "Only their own" },
];

const TZ = [
  ["America/New_York", "Eastern"],
  ["America/Chicago", "Central"],
  ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona (no DST)"],
  ["America/Los_Angeles", "Pacific"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
  ["UTC", "UTC"],
];

export default function ScheduleAccessSettings() {
  const [def, setDef] = useState("all");
  const [overrides, setOverrides] = useState({});
  const [crew, setCrew] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [overload, setOverload] = useState(3);
  const [crewConflict, setCrewConflict] = useState(true);
  const [custPreview, setCustPreview] = useState(null);
  const [awaiting, setAwaiting] = useState(null);
  const [reminders, setReminders] = useState({
    time: "07:00",
    tz: "America/Chicago",
    enabled: true,
    customer_enabled: false,
    customer_time: "08:00",
    customer_lead_days: 1,
    customer_stages: ["Template", "Install"],
    reschedule_alert: false,
  });

  useEffect(() => {
    api
      .get("/settings/schedule-visibility")
      .then(({ data }) => {
        setDef(data.default || "all");
        setOverrides(data.overrides || {});
        setCrew(data.crew || []);
        setOverload(data.overload_threshold || 3);
        setCrewConflict(data.crew_conflict_check !== false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/settings/reminder-preview")
      .then(({ data }) => setPreview(data))
      .catch(() => {});
    api
      .get("/settings/reminders")
      .then(({ data }) =>
        setReminders({
          time: data.time || "07:00",
          tz: data.tz || "America/Chicago",
          enabled: data.enabled !== false,
          customer_enabled: data.customer_enabled === true,
          customer_time: data.customer_time || "08:00",
          customer_lead_days:
            data.customer_lead_days == null ? 1 : data.customer_lead_days,
          customer_stages: data.customer_stages || ["Template", "Install"],
          reschedule_alert: data.reschedule_alert === true,
        }),
      )
      .catch(() => {});
    api
      .get("/settings/customer-reminder-preview")
      .then(({ data }) => setCustPreview(data))
      .catch(() => {});
    api
      .get("/schedule/awaiting-confirmation")
      .then(({ data }) => setAwaiting(data))
      .catch(() => {});
  }, []);

  const setMember = (email, value) =>
    setOverrides((o) => {
      const next = { ...o };
      if (value === "default") delete next[email];
      else next[email] = value;
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/schedule-visibility", {
        default: def,
        overrides,
        overload_threshold: overload,
        crew_conflict_check: crewConflict,
      });
      setDef(data.default);
      setOverrides(data.overrides || {});
      setOverload(data.overload_threshold || overload);
      setCrewConflict(data.crew_conflict_check !== false);
      await api.put("/settings/reminders", reminders);
      const { data: p } = await api.get("/settings/reminder-preview");
      setPreview(p);
      try {
        const { data: cp } = await api.get("/settings/customer-reminder-preview");
        setCustPreview(cp);
      } catch {
        /* non-blocking */
      }
      toast.success("Schedule access & reminders saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Schedule Access" />
      <main className="relative z-10 mx-auto max-w-2xl pb-20 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Settings
        </div>
        <h1
          className="text-2xl font-bold tracking-tight sm:text-3xl"
          data-testid="schedule-access-title"
        >
          Schedule Access
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Decide whether each crew member sees the whole shop's calendar and map,
          or only the jobs, tasks and visits assigned to them. Admins always see
          everything.
        </p>

        <CalendarSubscribe />

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Eye size={15} weight="bold" /> Default for everyone
          </div>
          <div className="mt-3 flex gap-2" data-testid="schedule-default-modes">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                data-testid={`schedule-default-${m.value}`}
                onClick={() => setDef(m.value)}
                className={`h-10 flex-1 rounded-lg border text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  def === m.value
                    ? "border-indigo-500 bg-indigo-500 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="crew-conflict-setting"
        >
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              data-testid="crew-conflict-toggle"
              checked={crewConflict}
              onChange={(e) => setCrewConflict(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-indigo-500"
            />
            <span>
              <span className="block text-xs font-bold uppercase tracking-widest text-zinc-300">
                Warn on crew double-booking
              </span>
              <span className="mt-1 block text-sm text-zinc-500">
                Flag on the calendar and project schedule when the same crew is
                booked on two jobs the same day and time window.
              </span>
            </span>
          </label>
        </div>

        <div
          className="mt-4 space-y-2"
          data-testid="schedule-access-crew-list"
        >
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-500">
            Per crew member
          </div>
          {crew.map((c) => {
            const admin = c.role === "admin";
            const value = admin ? "all" : overrides[c.email] || "default";
            return (
              <div
                key={c.email}
                data-testid={`schedule-access-member-${c.email}`}
                className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-zinc-100">
                    {c.name || c.email}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    {c.email} · {c.role}
                  </div>
                </div>
                {admin ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
                    <ShieldCheck size={13} weight="bold" /> Sees everything
                  </span>
                ) : (
                  <select
                    data-testid={`schedule-access-select-${c.email}`}
                    value={value}
                    onChange={(e) => setMember(c.email, e.target.value)}
                    className="h-9 rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
                  >
                    <option value="default">
                      Default ({def === "own" ? "Only their own" : "Whole shop"})
                    </option>
                    <option value="all">Whole shop</option>
                    <option value="own">Only their own</option>
                  </select>
                )}
              </div>
            );
          })}
          {crew.length === 0 && (
            <p className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500">
              No crew members yet.
            </p>
          )}
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="reminder-settings"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <BellRinging size={15} weight="bold" /> Morning reminder time
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Pick when crew get the morning text and the time zone that hour is
            measured in. Reminders go out once, at the top of that hour.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Send at
              </label>
              <input
                type="time"
                data-testid="reminder-time-input"
                value={reminders.time}
                onChange={(e) =>
                  setReminders((r) => ({ ...r, time: e.target.value }))
                }
                className="h-10 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Time zone
              </label>
              <select
                data-testid="reminder-tz-select"
                value={reminders.tz}
                onChange={(e) =>
                  setReminders((r) => ({ ...r, tz: e.target.value }))
                }
                className="h-10 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
              >
                {TZ.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Reminders
              </label>
              <button
                type="button"
                data-testid="reminder-enabled-toggle"
                onClick={() =>
                  setReminders((r) => ({ ...r, enabled: !r.enabled }))
                }
                className={`h-10 w-full rounded-lg border text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  reminders.enabled
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                    : "border-zinc-800 bg-zinc-950 text-zinc-500"
                }`}
              >
                {reminders.enabled ? "On" : "Off"}
              </button>
            </div>
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="customer-reminder-settings"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <BellRinging size={15} weight="bold" /> Customer appointment reminders
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Auto-text each customer their appointment (date, time and arrival window)
            the morning before. Uses the time zone above, sends once per appointment,
            and skips anyone with no phone on file.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Send at
              </label>
              <input
                type="time"
                data-testid="customer-reminder-time-input"
                value={reminders.customer_time}
                onChange={(e) =>
                  setReminders((r) => ({ ...r, customer_time: e.target.value }))
                }
                className="h-10 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Days before
              </label>
              <input
                type="number"
                min={0}
                max={14}
                data-testid="customer-reminder-lead-input"
                value={reminders.customer_lead_days}
                onChange={(e) =>
                  setReminders((r) => ({
                    ...r,
                    customer_lead_days: Math.max(
                      0,
                      Math.min(14, Number(e.target.value) || 0),
                    ),
                  }))
                }
                className="h-10 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Reminders
              </label>
              <button
                type="button"
                data-testid="customer-reminder-enabled-toggle"
                onClick={() =>
                  setReminders((r) => ({
                    ...r,
                    customer_enabled: !r.customer_enabled,
                  }))
                }
                className={`h-10 w-full rounded-lg border text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                  reminders.customer_enabled
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-300"
                    : "border-zinc-800 bg-zinc-950 text-zinc-500"
                }`}
              >
                {reminders.customer_enabled ? "On" : "Off"}
              </button>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">
              Remind about
            </label>
            <div
              className="mt-1 flex flex-wrap gap-2"
              data-testid="customer-reminder-stages"
            >
              {["Template", "Fabrication", "Install"].map((st) => {
                const on = reminders.customer_stages.includes(st);
                return (
                  <button
                    key={st}
                    type="button"
                    data-testid={`customer-reminder-stage-${st}`}
                    onClick={() =>
                      setReminders((r) => ({
                        ...r,
                        customer_stages: on
                          ? r.customer_stages.filter((x) => x !== st)
                          : [...r.customer_stages, st],
                      }))
                    }
                    className={`h-9 rounded-lg border px-3 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                      on
                        ? "border-indigo-500 bg-indigo-500/15 text-indigo-300"
                        : "border-zinc-800 bg-zinc-950 text-zinc-500"
                    }`}
                  >
                    {st}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            data-testid="reschedule-alert-toggle"
            onClick={() =>
              setReminders((r) => ({ ...r, reschedule_alert: !r.reschedule_alert }))
            }
            className={`mt-3 flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-xs font-semibold transition-colors duration-150 ${
              reminders.reschedule_alert
                ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                : "border-zinc-800 bg-zinc-950 text-zinc-400"
            }`}
          >
            <span className="normal-case tracking-normal">
              Text customer when a booked date moves
            </span>
            <span className="uppercase tracking-widest">
              {reminders.reschedule_alert ? "On" : "Off"}
            </span>
          </button>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="customer-reminder-preview"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Eye size={15} weight="bold" /> Who gets texted next
            {custPreview?.date ? ` — ${custPreview.date}` : ""}
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Customers scheduled {custPreview?.lead_days ?? 1} day(s) out who haven't
            confirmed yet — they'll get the reminder at {custPreview?.time || "08:00"}.
            {custPreview && !custPreview.enabled
              ? " Reminders are currently OFF, so nothing will send."
              : ""}
          </p>
          <div className="mt-3 space-y-2">
            {(custPreview?.items || []).length === 0 && (
              <div className="rounded-lg border border-dashed border-zinc-800 p-4 text-sm text-zinc-500">
                No customers to remind for that day.
              </div>
            )}
            {(custPreview?.items || []).map((it, i) => (
              <div
                key={i}
                data-testid={`customer-reminder-preview-item-${i}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-zinc-100">
                    {it.customer_name || it.code}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    {it.kind} · {it.code}
                    {it.address ? ` · ${it.address}` : ""}
                  </div>
                </div>
                <span
                  className={`shrink-0 text-xs ${
                    it.no_phone ? "text-amber-300" : "text-zinc-500"
                  }`}
                >
                  {it.no_phone ? "No phone" : it.phone}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="awaiting-confirmation"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Warning size={15} weight="bold" /> Awaiting confirmation
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Upcoming Template/Install appointments (next 2 weeks) the customer hasn't
            confirmed by text. Give them a call to lock it in.
          </p>
          <div className="mt-3 space-y-2">
            {(awaiting?.items || []).length === 0 && (
              <div className="rounded-lg border border-dashed border-zinc-800 p-4 text-sm text-zinc-500">
                Everyone with an upcoming appointment has confirmed.
              </div>
            )}
            {(awaiting?.items || []).map((it, i) => (
              <div
                key={i}
                data-testid={`awaiting-confirmation-item-${i}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-zinc-100">
                    {it.customer_name || it.code}
                  </div>
                  <div className="truncate text-xs text-zinc-500">
                    {it.kind} · {it.date} ·{" "}
                    {it.days_until === 0 ? "today" : `in ${it.days_until}d`}
                  </div>
                </div>
                {it.phone ? (
                  <a
                    href={`tel:${it.phone.replace(/[^\d+]/g, "")}`}
                    data-testid={`awaiting-call-${i}`}
                    className="shrink-0 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20"
                  >
                    Call
                  </a>
                ) : (
                  <span className="shrink-0 text-xs text-amber-300">No phone</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="overload-settings"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Warning size={15} weight="bold" /> Team Load — overloaded day
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            Flag a crew as overloaded on the Team Load view when they have this
            many stops or more on a single day.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={20}
              data-testid="overload-threshold-input"
              value={overload}
              onChange={(e) =>
                setOverload(
                  Math.max(1, Math.min(20, Number(e.target.value) || 1)),
                )
              }
              className="h-10 w-24 rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
            />
            <span className="text-xs text-zinc-500">
              stops per day = overloaded
            </span>
          </div>
        </div>

        <div
          className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="reminder-preview"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <Eye size={15} weight="bold" /> Morning reminders
            {preview?.date ? ` — ${preview.date}` : ""}
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            The exact text each crew member or team gets the morning of their
            task. Nothing is sent from this screen.
          </p>
          <div className="mt-3 space-y-2">
            {(preview?.items || []).length === 0 && (
              <div className="rounded-lg border border-dashed border-zinc-800 p-4 text-sm text-zinc-500">
                No reminders scheduled for today. Example text:
                <div className="mt-2 rounded-md bg-zinc-950 p-2 font-mono-data text-xs text-zinc-300">
                  {preview?.sample || "—"}
                </div>
              </div>
            )}
            {(preview?.items || []).map((it, i) => (
              <div
                key={i}
                data-testid={`reminder-item-${i}`}
                className="rounded-lg border border-zinc-800 bg-zinc-950 p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
                    {it.is_team && (
                      <span className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-indigo-200">
                        Team
                      </span>
                    )}
                    {it.recipient}
                  </span>
                  <span className="shrink-0 text-xs text-zinc-500">
                    {it.phones.length
                      ? it.phones.join(", ")
                      : "No phone on file"}
                  </span>
                </div>
                <div className="mt-1 font-mono-data text-xs text-zinc-300">
                  {it.message}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Button
            type="button"
            data-testid="schedule-access-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" /> Save
          </Button>
        </div>
      </main>
    </div>
  );
}
